/**
 * Schema for the System Settings page.
 *
 * Every setting the backend reads is listed here, keyed by its exact param_key.
 * The dashboard renders ONLY what is listed below, using the declared type, so
 * admins edit values with the right control instead of a raw text box.
 *
 * The types are not guesses: they mirror how the backend parses each value.
 *   Integer.parseInt(...)   -> "integer"
 *   Double.parseDouble(...) -> "decimal"
 *   parsed as a 0/1 flag    -> "boolean"
 *   used as a raw string    -> "text" / "textarea"
 *
 * TO ADD A NEW SETTING:
 *   1. Insert the row into `admin_param` for every country (and every tenant DB).
 *   2. Add the backend code that reads the key.
 *   3. Add one entry here. That is all the dashboard needs.
 *
 * Admins cannot add or remove settings from the UI by design - a row with no
 * backend code reading it does nothing.
 *
 * Field reference:
 *   label    (required) English name shown to the admin. Never show the key.
 *   type     (required) integer | decimal | boolean | text | textarea | select
 *   group    (required) tab id, must exist in CONFIG_GROUPS
 *   section             sub-heading inside the tab, for long tabs
 *   unit                suffix shown inside the input, e.g. "%", "hours"
 *   min/max/step        numeric bounds, enforced before save
 *   options             for type "select": [{ value, label }]
 *   help                extra hint. Falls back to the DB description (paramName).
 *   field               "title" | "message" - pairs two rows into one message
 *                       block. Both rows must share the same `label`.
 *   optional            do not flag as "Not set" when absent. Use for keys the
 *                       backend reads for one country only, or not at all.
 */

export const CONFIG_GROUPS = [
  {
    id: "orders",
    label: "Orders & Pricing",
    description: "What a customer is charged and the limits on what they can order.",
    sections: [
      { label: "Pricing & Tax" },
      { label: "Order Limits" },
      {
        label: "Walk-In (POS)",
        description: "Applies only to orders taken at the counter, not app orders.",
      },
    ],
  },
  {
    id: "delivery",
    label: "Delivery",
    description: "Delivery charges and the order value that earns free delivery.",
  },
  {
    id: "scheduling",
    label: "Scheduling",
    description:
      "How far ahead orders must be booked, and how long customers have to change their mind.",
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    description: "Plan length, renewal timing and reminders for subscription customers.",
  },
  {
    id: "wallet",
    label: "Wallet & Referrals",
    description: "Top-up limits, bonus credit, and the rewards for referring a friend.",
    sections: [
      { label: "Wallet" },
      { label: "Referrals" },
      {
        label: "Win-Back Coupons",
        description:
          "Automatic coupons sent to customers who have not ordered in a while.",
      },
    ],
  },
  {
    id: "features",
    label: "Features",
    description: "Turn parts of the customer app on or off.",
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "The push notifications sent to customers and drivers.",
    sections: [
      { label: "Order Progress", description: "Sent as an order moves through the normal flow." },
      { label: "Order Changes & Issues", description: "Sent when something changes or goes wrong." },
      { label: "Subscriptions" },
      { label: "Driver Alerts", description: "Sent to the driver app, not to customers." },
      { label: "Account & Rewards" },
    ],
  },
  {
    id: "advanced",
    label: "Advanced",
    description:
      "Technical settings tied to how the system is wired. Change these only if you know what depends on them.",
    sections: [
      {
        label: "Linked Services",
        description:
          "Each must match the ID of an existing service. Check the Services page before changing any of these.",
      },
      { label: "App Versions", description: "Customers on a lower build are prompted to update." },
      { label: "Login Security", description: "Applies to dashboard users, not customers." },
      { label: "Data Retention" },
      { label: "Email" },
    ],
  },
];

/**
 * Rows that exist in `admin_param` but are dead, and are hidden from the page
 * entirely rather than falling through to the "Other" tab.
 *
 * Verified across the backend, this dashboard, and the customer, driver and
 * laundry apps - no code reads any of them:
 *   CASHBACK_EXPIRY
 *     Only appears in a commented-out line, OrderController.java:1332.
 *   WALLET_AMOUNT_CAP
 *     Only appears in a commented-out line,
 *     UserWalletTransactionController.java:227.
 *   PRESS_SERVICE_ID / DELICATES_SERVICE_ID
 *     The customer app declares `pressServiceId` / `delicatesServiceId` in
 *     objects/config.dart, but the backend never emits those fields in
 *     getPrimaryParams, so they parse as null and nothing reads them.
 *   ADMIN_EMAILS
 *     No reference anywhere, in any form.
 *
 * The database rows are left untouched. Delete this entry if a key is ever
 * wired up again.
 */
export const IGNORED_KEYS = new Set([
  "CASHBACK_EXPIRY",
  "WALLET_AMOUNT_CAP",
  "PRESS_SERVICE_ID",
  "DELICATES_SERVICE_ID",
  "ADMIN_EMAILS",
]);

export const CONFIG_SCHEMA = {
  /* ------------------------------------------------------- Orders & Pricing */
  VAT_PERCENTAGE: {
    section: "Pricing & Tax",
    label: "VAT Percentage",
    type: "decimal",
    group: "orders",
    unit: "%",
    min: 0,
    max: 100,
    help: "Tax added to every order, subscription and top-up.",
  },
  MINIMUM_AMOUNT_PER_ORDER: {
    section: "Pricing & Tax",
    label: "Minimum Order Amount",
    type: "decimal",
    group: "orders",
    min: 0,
    help: "Customers cannot check out below this total.",
  },
  MAX_BAGS: {
    section: "Order Limits",
    label: "Maximum Bags Per Order",
    type: "integer",
    group: "orders",
    min: 1,
  },
  MAX_ITEMS: {
    section: "Order Limits",
    label: "Maximum Items Per Order",
    type: "integer",
    group: "orders",
    min: 1,
  },
  RECURRANCE_MAXIMUM: {
    section: "Order Limits",
    label: "Maximum Repeat Orders",
    type: "integer",
    group: "orders",
    min: 0,
    help: "How many times a single order can be set to repeat.",
  },
  POS_VAT_PERCENTAGE: {
    section: "Walk-In (POS)",
    label: "VAT Percentage (Walk-In)",
    type: "decimal",
    group: "orders",
    unit: "%",
    min: 0,
    max: 100,
    help: "Tax applied to counter orders. Kept separate from the app VAT above.",
  },
  POS_EXPRESS_PERCENTAGE: {
    section: "Walk-In (POS)",
    label: "Express Surcharge (Walk-In)",
    type: "decimal",
    group: "orders",
    unit: "%",
    min: 0,
    max: 100,
    help: "Extra percentage added to walk-in orders marked as express.",
  },

  /* --------------------------------------------------------------- Delivery */
  DELIVERY_FEE_NON_PRO: {
    label: "Delivery Fee - Standard Customer",
    type: "decimal",
    group: "delivery",
    min: 0,
  },
  DELIVERY_FEE_PRO: {
    label: "Delivery Fee - Pro Customer",
    type: "decimal",
    group: "delivery",
    min: 0,
  },
  MINIMUM_FREE_WITHOUT_FEE_NON_PRO: {
    label: "Free Delivery Above - Standard Customer",
    type: "decimal",
    group: "delivery",
    min: 0,
    help: "Orders at or above this total ship free.",
  },
  MINIMUM_FREE_WITHOUT_FEE_PRO: {
    label: "Free Delivery Above - Pro Customer",
    type: "decimal",
    group: "delivery",
    min: 0,
    help: "Orders at or above this total ship free.",
  },

  /* ------------------------------------------------------------- Scheduling */
  GRACE_BEFORE_PICKUP: {
    label: "Notice Required Before Pickup",
    type: "integer",
    group: "scheduling",
    unit: "hours",
    min: 0,
    help: "How far ahead a pickup slot must be booked.",
  },
  MINIMUM_DIFFERENCE_BETWEEN_PICK_DROP: {
    label: "Minimum Turnaround Time",
    type: "integer",
    group: "scheduling",
    unit: "hours",
    min: 0,
    help: "Shortest gap allowed between pickup and drop-off.",
  },
  UPDATE_GRACE: {
    label: "Customers Can Edit Until",
    type: "integer",
    group: "scheduling",
    unit: "hours before pickup",
    min: 0,
  },
  CANCELLATION_GRACE: {
    label: "Customers Can Cancel Until",
    type: "integer",
    group: "scheduling",
    unit: "hours before pickup",
    min: 0,
  },
  PENDING_CHECKOUT_FOR_MINUTES: {
    label: "Remind About Unfinished Checkout After",
    type: "integer",
    group: "scheduling",
    unit: "minutes",
    min: 1,
  },

  /* ---------------------------------------------------------- Subscriptions */
  NUMBER_OF_WEEKS_PER_SUBSCRIPTION: {
    label: "Subscription Length",
    type: "integer",
    group: "subscriptions",
    unit: "weeks",
    min: 1,
  },
  SUB_DAYS_BEFORE_FIRST: {
    label: "Notice Before First Pickup",
    type: "integer",
    group: "subscriptions",
    unit: "days",
    min: 0,
  },
  SUB_MINIMUM_DIFFERENCE_BETWEEN_PICK_DROP: {
    label: "Minimum Turnaround Time",
    type: "integer",
    group: "subscriptions",
    unit: "hours",
    min: 0,
    help: "Shortest gap between pickup and drop-off on a subscription order.",
  },
  LAST_DATE_FOR_PICKUP_AFTER_SUBSCRIPTION_EXPIRY: {
    label: "Pickups Allowed After Expiry",
    type: "integer",
    group: "subscriptions",
    unit: "days",
    min: 0,
  },
  LAST_DATE_FOR_DROPOFF_AFTER_SUBSCRIPTION_EXPIRY: {
    label: "Drop-offs Allowed After Expiry",
    type: "integer",
    group: "subscriptions",
    unit: "days",
    min: 0,
  },
  RENEWAL_REMINDER_DAYS: {
    label: "Send Renewal Reminder",
    type: "integer",
    group: "subscriptions",
    unit: "days before expiry",
    min: 0,
  },
  RENEW_BEFORE_DAYS: {
    label: "Auto-Renew Runs",
    type: "integer",
    group: "subscriptions",
    unit: "days before expiry",
    min: 0,
  },
  RENEW_PRO_PACKAGE_DAYS_BEFORE: {
    label: "Pro Plan Auto-Renew Runs",
    type: "integer",
    group: "subscriptions",
    unit: "days before expiry",
    min: 0,
  },
  SUBSCRIBERS_COUPON_CODE: {
    label: "Subscriber Coupon Code",
    type: "text",
    group: "subscriptions",
    help: "Coupon automatically available to subscription customers.",
  },

  /* ------------------------------------------------------ Wallet & Referrals */
  MINIMUM_TOP_UP_AMOUNT: {
    section: "Wallet",
    label: "Minimum Top-Up",
    type: "decimal",
    group: "wallet",
    min: 0,
  },
  TOP_UP_EXTRA_CREDITS: {
    section: "Wallet",
    label: "Bonus Credit On Top-Up",
    type: "decimal",
    group: "wallet",
    min: 0,
  },
  REFERRING_USER_BONUS_CREDIT: {
    section: "Referrals",
    label: "Reward For Referring",
    type: "decimal",
    group: "wallet",
    min: 0,
    help: "Credit given to the customer who sent the referral.",
  },
  REFERRED_NEW_USER_BONUS: {
    section: "Referrals",
    label: "Reward For Joining Via Referral",
    type: "decimal",
    group: "wallet",
    min: 0,
    help: "Credit given to the new customer who was referred.",
  },
  REFERRAL_SHARE_APP_TEXT: {
    section: "Referrals",
    label: "Referral Invite Message",
    type: "textarea",
    group: "wallet",
    help: "Sent when a customer shares their referral code. Use #REFERRAL_CODE# where the code should appear.",
  },
  SHARE_APP_TEXT: {
    section: "Referrals",
    label: "Share App Message",
    type: "textarea",
    group: "wallet",
    help: "Sent when a customer shares the app without a referral code.",
  },

  NO_ORDER_SINCE: {
    section: "Win-Back Coupons",
    label: "Consider A Customer Inactive After",
    type: "integer",
    group: "wallet",
    unit: "days",
    min: 1,
    optional: true,
  },
  NO_ORDER_COUPON_PERCENTAGE: {
    section: "Win-Back Coupons",
    label: "Win-Back Coupon Discount",
    type: "decimal",
    group: "wallet",
    unit: "%",
    min: 0,
    max: 100,
    optional: true,
  },
  NO_ORDER_COUPON_EXPIRE: {
    section: "Win-Back Coupons",
    label: "Win-Back Coupon Valid For",
    type: "integer",
    group: "wallet",
    unit: "days",
    min: 1,
    optional: true,
  },

  /* --------------------------------------------------------------- Features */
  DEFAULT_USER_STATUS: {
    label: "New Customer Account Status",
    type: "select",
    group: "features",
    options: [
      { value: "1", label: "Active immediately" },
      { value: "0", label: "Waiting list" },
    ],
    help: "Whether a customer can order as soon as they register.",
  },
  SHOW_SUBSCRIPTION_BLOCK: {
    label: "Show Subscriptions In App",
    type: "boolean",
    group: "features",
  },
  SHOW_SUBSCRIBE_TO_PRO_BANNER: {
    label: "Show Pro Upgrade Banner",
    type: "boolean",
    group: "features",
  },
  SUBSCRIPTION_PILOT: {
    label: "Limit Subscriptions To Pilot Customers",
    type: "boolean",
    group: "features",
    help: "When on, only pilot customers can subscribe.",
  },
  SHOES_PILOT: {
    label: "Limit Shoe Cleaning To Pilot Customers",
    type: "boolean",
    group: "features",
    help: "When on, only pilot customers can order shoe cleaning.",
  },

  /* ---------------------------------------------------------- Notifications */
  ORDER_OUT_FOR_PICKUP_NOTIF_TITLE: { label: "Driver On The Way", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_OUT_FOR_PICKUP_NOTIF_BODY: { label: "Driver On The Way", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },
  ORDER_PICKED_UP_NOTIF_TITLE: { label: "Laundry Collected", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_PICKED_UP_NOTIF_BODY: { label: "Laundry Collected", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },
  ORDER_BEING_WASHED_NOTIF_TITLE: { label: "Washing Started", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_BEING_WASHED_NOTIF_BODY: { label: "Washing Started", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },
  ORDER_WASHED_NOTIF_TITLE: { label: "Washing Finished", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_WASHED_NOTIF_BODY: { label: "Washing Finished", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },
  ORDER_OUT_FOR_DELIVERY_NOTIF_TITLE: { label: "Out For Delivery", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_OUT_FOR_DELIVERY_NOTIF_BODY: { label: "Out For Delivery", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },
  ORDER_DELIVERED_NOTIF_TITLE: { label: "Order Delivered", field: "title", type: "text", group: "notifications", section: "Order Progress" },
  ORDER_DELIVERED_NOTIF_BODY: { label: "Order Delivered", field: "message", type: "textarea", group: "notifications", section: "Order Progress" },

  UPDATE_ORDER_NOTIF_TITLE: { label: "Order Updated", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  UPDATE_ORDER_NOTIF_BODY: { label: "Order Updated", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CANCELLED_BY_ADMIN_NOTIF_TITLE: { label: "Order Cancelled By Staff", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CANCELLED_BY_ADMIN_NOTIF_BODY: { label: "Order Cancelled By Staff", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_QNT_UPD_PAY_NOTIFICATION_TITLE: { label: "Extra Payment Needed", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_QNT_UPD_PAY_NOTIFICATION_BODY: { label: "Extra Payment Needed", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_QNT_UPD_RFN_NOTIFICATION_TITLE: { label: "Refund Due", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_QNT_UPD_RFN_NOTIFICATION_BODY: { label: "Refund Due", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CUST_NOT_AVL_PK_NOTIF_TITLE: { label: "Nobody Home At Pickup", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CUST_NOT_AVL_PK_NOTIF_BODY: { label: "Nobody Home At Pickup", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CUST_NOT_AVL_DO_NOTIF_TITLE: { label: "Nobody Home At Delivery", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_CUST_NOT_AVL_DO_NOTIF_BODY: { label: "Nobody Home At Delivery", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_HAS_PRE_WASH_PHOTO_TITLE: { label: "Before-Wash Photo Added", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_HAS_PRE_WASH_PHOTO_BODY: { label: "Before-Wash Photo Added", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  ORDER_HAS_POST_WASH_PHOTO_TITLE: { label: "After-Wash Photo Added", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  ORDER_HAS_POST_WASH_PHOTO_BODY: { label: "After-Wash Photo Added", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },
  PENDING_CHECKOUT_NOTIFICATION_TITLE: { label: "Unfinished Checkout", field: "title", type: "text", group: "notifications", section: "Order Changes & Issues" },
  PENDING_CHECKOUT_NOTIFICATION_BODY: { label: "Unfinished Checkout", field: "message", type: "textarea", group: "notifications", section: "Order Changes & Issues" },

  RENEWAL_REMINDER_NOTIFICATION_TITLE: { label: "Renewal Reminder", field: "title", type: "text", group: "notifications", section: "Subscriptions" },
  RENEWAL_REMINDER_NOTIFICATION_BODY: { label: "Renewal Reminder", field: "message", type: "textarea", group: "notifications", section: "Subscriptions" },
  FAILED_RENEWAL_NOTIFICATION_TITLE: { label: "Renewal Failed", field: "title", type: "text", group: "notifications", section: "Subscriptions" },
  FAILED_RENEWAL_NOTIFICATION_BODY: { label: "Renewal Failed", field: "message", type: "textarea", group: "notifications", section: "Subscriptions" },
  PRO_RENEWAL_REMINDER_NOTIFICATION_TITLE: { label: "Pro Renewal Reminder", field: "title", type: "text", group: "notifications", section: "Subscriptions" },
  PRO_RENEWAL_REMINDER_NOTIFICATION_BODY: { label: "Pro Renewal Reminder", field: "message", type: "textarea", group: "notifications", section: "Subscriptions" },
  PRO_FAILED_RENEWAL_NOTIFICATION_TITLE: { label: "Pro Renewal Failed", field: "title", type: "text", group: "notifications", section: "Subscriptions" },
  PRO_FAILED_RENEWAL_NOTIFICATION_BODY: { label: "Pro Renewal Failed", field: "message", type: "textarea", group: "notifications", section: "Subscriptions" },

  DRIVER_ASSIGNED_ORDER_NOTIF_TITLE: { label: "New Order Assigned", field: "title", type: "text", group: "notifications", section: "Driver Alerts" },
  DRIVER_ASSIGNED_ORDER_NOTIF_BODY: { label: "New Order Assigned", field: "message", type: "textarea", group: "notifications", section: "Driver Alerts" },
  DRIVER_ASSIGNED_WK_NOTIF_TITLE: { label: "Welcome Kit Assigned", field: "title", type: "text", group: "notifications", section: "Driver Alerts" },
  DRIVER_ASSIGNED_WK_NOTIF_BODY: { label: "Welcome Kit Assigned", field: "message", type: "textarea", group: "notifications", section: "Driver Alerts" },
  DRIVER_ORDER_WASHED_NOTIF_TITLE: { label: "Order Ready For Delivery", field: "title", type: "text", group: "notifications", section: "Driver Alerts" },
  DRIVER_ORDER_WASHED_NOTIF_BODY: { label: "Order Ready For Delivery", field: "message", type: "textarea", group: "notifications", section: "Driver Alerts" },
  DRIVER_CANCELLED_ORDER_NOTIF_TITLE: { label: "Assigned Order Cancelled", field: "title", type: "text", group: "notifications", section: "Driver Alerts" },
  DRIVER_CANCELLED_ORDER_NOTIF_BODY: { label: "Assigned Order Cancelled", field: "message", type: "textarea", group: "notifications", section: "Driver Alerts" },

  USER_ACTIVATED_NOTIF_TITLE: { label: "Account Activated", field: "title", type: "text", group: "notifications", section: "Account & Rewards" },
  USER_ACTIVATED_NOTIF_BODY: { label: "Account Activated", field: "message", type: "textarea", group: "notifications", section: "Account & Rewards" },
  DORMANT_USER_TITLE: { label: "Win-Back Coupon Sent", field: "title", type: "text", group: "notifications", section: "Account & Rewards" },
  DORMANT_USER_BODY: { label: "Win-Back Coupon Sent", field: "message", type: "textarea", group: "notifications", section: "Account & Rewards" },
  BONUS_EARNED_NOTIFICATION_TITLE: { label: "Referral Bonus Earned", field: "title", type: "text", group: "notifications", section: "Account & Rewards" },
  BONUS_EARNED_NOTIFICATION_BODY: { label: "Referral Bonus Earned", field: "message", type: "textarea", group: "notifications", section: "Account & Rewards" },

  /* --------------------------------------------------------------- Advanced */
  PER_ITEM_BAG_SERVICE_ID: {
    label: "Per-Item Bag Service ID",
    type: "integer",
    group: "advanced",
    section: "Linked Services",
    min: 0,
  },
  SHOES_SERVICE_ID: {
    label: "Shoe Cleaning Service ID",
    type: "integer",
    group: "advanced",
    section: "Linked Services",
    min: 0,
  },
  WELCOME_KIT_SERVICE_ID: {
    label: "Welcome Kit Service ID",
    type: "integer",
    group: "advanced",
    section: "Linked Services",
    min: 0,
  },
  REWASH_SERVICE_ID: {
    label: "Rewash Service ID",
    type: "integer",
    group: "advanced",
    section: "Linked Services",
    min: 0,
  },
  LATEST_ANDROID_BUILD: {
    label: "Latest Android Build Number",
    type: "integer",
    group: "advanced",
    section: "App Versions",
    min: 0,
  },
  LATEST_IOS_BUILD: {
    label: "Latest iOS Build Number",
    type: "integer",
    group: "advanced",
    section: "App Versions",
    min: 0,
  },
  CONSECUTIVE_FAILED_LOGINS_LOCK_COUNT: {
    label: "Failed Logins Before Lockout",
    type: "integer",
    group: "advanced",
    section: "Login Security",
    min: 1,
  },
  LOCKOUT_PERIOD_IN_MINUTES: {
    label: "Lockout Duration",
    type: "integer",
    group: "advanced",
    section: "Login Security",
    unit: "minutes",
    min: 1,
  },
  ACTIVITY_LOGS_CLEAR_DAYS: {
    label: "Keep Activity Logs For",
    type: "integer",
    group: "advanced",
    section: "Data Retention",
    unit: "days",
    min: 1,
  },
  APP_LOGS_CLEAR_DAYS: {
    label: "Keep App Logs For",
    type: "integer",
    group: "advanced",
    section: "Data Retention",
    unit: "days",
    min: 1,
  },
  BCC_ORDER_EMAILS: {
    label: "BCC Address For Order Emails",
    type: "text",
    group: "advanced",
    section: "Email",
    help: "Comma-separated addresses copied on every order email.",
  },
};

/** Values stored for boolean settings. The backend reads these with Integer.parseInt. */
export const BOOL_ON = "1";
export const BOOL_OFF = "0";

export const isBoolOn = (value) => String(value ?? "").trim() === BOOL_ON;

/**
 * Validates a value against its schema entry.
 * Returns an error string, or null when the value is acceptable.
 */
export const validateConfigValue = (schema, rawValue) => {
  const value = String(rawValue ?? "").trim();

  if (schema.type === "boolean") return null;

  if (value === "") return "This field cannot be empty.";

  if (schema.type === "integer" || schema.type === "decimal") {
    const pattern = schema.type === "integer" ? /^-?\d+$/ : /^-?\d+(\.\d+)?$/;
    if (!pattern.test(value)) {
      return schema.type === "integer"
        ? "Enter a whole number."
        : "Enter a number.";
    }
    const num = Number(value);
    if (schema.min != null && num < schema.min) {
      return `Cannot be less than ${schema.min}.`;
    }
    if (schema.max != null && num > schema.max) {
      return `Cannot be more than ${schema.max}.`;
    }
  }

  if (schema.type === "select" && schema.options) {
    if (!schema.options.some((option) => String(option.value) === value)) {
      return "Select one of the available options.";
    }
  }

  return null;
};

/**
 * Splits the rows returned by the API into the tabs declared above.
 * Anything not in the schema lands in `unknown` so it stays visible and
 * editable as plain text rather than silently disappearing.
 */
/** Declaration order in CONFIG_SCHEMA is the display order. */
const KEY_ORDER = Object.keys(CONFIG_SCHEMA).reduce((acc, key, index) => {
  acc[key] = index;
  return acc;
}, {});

export const groupConfigRows = (rows) => {
  const grouped = {};
  const unknown = [];

  rows.forEach((row) => {
    if (IGNORED_KEYS.has(row.paramKey)) return;
    const schema = CONFIG_SCHEMA[row.paramKey];
    if (!schema) {
      unknown.push(row);
      return;
    }
    if (!grouped[schema.group]) grouped[schema.group] = [];
    grouped[schema.group].push({ ...row, schema });
  });

  // The API returns rows in database order, which has nothing to do with how
  // they should read. Sort by where each key sits in the schema.
  Object.keys(grouped).forEach((group) => {
    grouped[group].sort(
      (a, b) => KEY_ORDER[a.paramKey] - KEY_ORDER[b.paramKey]
    );
  });

  return { grouped, unknown };
};

/**
 * Collapses `field: "title" | "message"` rows that share a label into a single
 * block, so a notification reads as one message rather than two settings.
 * Everything else passes through untouched.
 */
export const pairConfigRows = (rows) => {
  const items = [];
  const byLabel = new Map();

  rows.forEach((row) => {
    // Rows on the "Other" tab have no schema entry at all.
    if (!row.schema?.field) {
      items.push({ kind: "single", row, key: row.paramKey });
      return;
    }
    const existing = byLabel.get(row.schema.label);
    if (existing) {
      existing[row.schema.field] = row;
      return;
    }
    const pair = {
      kind: "pair",
      key: row.schema.label,
      label: row.schema.label,
      [row.schema.field]: row,
    };
    byLabel.set(row.schema.label, pair);
    items.push(pair);
  });

  return items;
};

/**
 * Orders a tab's rows into its declared sections. Rows with no section, and
 * sections not declared on the group, still come through so nothing is lost.
 */
export const sectionConfigRows = (group, rows) => {
  const bySection = new Map();
  const meta = new Map();

  (group.sections ?? []).forEach((section) => {
    bySection.set(section.label, []);
    meta.set(section.label, section);
  });

  rows.forEach((row) => {
    const label = row.schema?.section ?? "";
    if (!bySection.has(label)) {
      bySection.set(label, []);
      meta.set(label, { label });
    }
    bySection.get(label).push(row);
  });

  return Array.from(bySection.entries())
    .filter(([, sectionRows]) => sectionRows.length > 0)
    .map(([label, sectionRows]) => ({
      section: meta.get(label) ?? { label },
      rows: sectionRows,
    }));
};

/**
 * Schema keys with no matching row for the selected country. `optional: true`
 * keys are skipped - they are only read for specific countries, or not read at
 * all, so flagging them as missing everywhere would be noise.
 */
export const findMissingKeys = (rows) => {
  const present = new Set(rows.map((row) => row.paramKey));
  return Object.keys(CONFIG_SCHEMA).filter(
    (key) => !present.has(key) && !CONFIG_SCHEMA[key].optional
  );
};
